import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { API_BASE } from "../DataContext";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildBlogPostingLD, buildBreadcrumbLD } from "../lib/jsonld";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageMeta(post ? post.title : "Blog", post ? post.excerpt : undefined);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    fetch(`${API_BASE}/api/blog/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setPost)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-[88px] md:pt-[112px] min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-black text-[#0a2a5e] mb-3">Article not found</h1>
          <Button asChild className="bg-[#0a2a5e] text-white rounded-full px-5"><Link to="/blog">Back to Blog</Link></Button>
        </div>
      </div>
    );
  }

  if (!post) return <div className="pt-[88px] md:pt-[112px] min-h-[60vh] flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="pt-[88px] md:pt-[112px]" data-testid="blog-post-page">
      <JsonLd blocks={[buildBlogPostingLD(post), buildBreadcrumbLD([
        { name: "Home", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: post.title, path: `/blog/${post.slug}` },
      ])]} id={`blog-post-${post.slug}`} />
      <section className="bg-[#0a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs text-sky-300 hover:text-white transition-colors mb-5" data-testid="blog-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.map((t) => (
              <span key={t} className="text-[11px] font-semibold text-sky-200 bg-white/10 border border-white/15 rounded-full px-2.5 py-0.5">{t}</span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">{post.title}</h1>
          <div className="flex items-center gap-5 mt-5 text-sm text-sky-200">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(post.created_at)}</span>
          </div>
        </div>
      </section>

      {post.coverImage && (
        <div className="max-w-4xl mx-auto px-6 -mt-0 md:-mt-6 relative z-10">
          <img src={post.coverImage} alt={post.title} className="w-full rounded-2xl shadow-xl" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <div className="prose-baishalii">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: (props) => <h2 className="text-xl md:text-2xl font-black text-[#0a2a5e] mt-10 mb-4" {...props} />,
              h3: (props) => <h3 className="text-lg font-bold text-[#0a2a5e] mt-8 mb-3" {...props} />,
              p: (props) => <p className="text-slate-700 leading-relaxed mb-5" {...props} />,
              ul: (props) => <ul className="list-disc pl-6 space-y-2 mb-5 text-slate-700" {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 space-y-2 mb-5 text-slate-700" {...props} />,
              strong: (props) => <strong className="font-bold text-[#0a2a5e]" {...props} />,
              table: (props) => <div className="overflow-x-auto mb-6"><table className="w-full text-sm border border-slate-200 rounded-lg" {...props} /></div>,
              th: (props) => <th className="bg-slate-50 text-left px-4 py-2.5 font-bold text-[#0a2a5e] border-b border-slate-200" {...props} />,
              td: (props) => <td className="px-4 py-2.5 border-b border-slate-100 text-slate-600" {...props} />
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#0a2a5e] to-sky-700 rounded-2xl p-8 text-center">
          <h3 className="text-white text-xl font-black mb-2">Have a project in mind?</h3>
          <p className="text-sky-100 text-sm mb-5">Talk to our team about pipes, tanks and fittings for your requirement.</p>
          <Button asChild className="bg-white text-[#0a2a5e] hover:bg-sky-100 rounded-full px-6 font-bold">
            <Link to="/contact" data-testid="blog-cta-contact">Get in Touch</Link>
          </Button>
        </div>
      </article>
    </div>
  );
}
